import find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import compact from 'lodash/compact';
import without from 'lodash/without';
import map from 'lodash/map';
import flatten from 'lodash/flatten';
import UserDatabases from '@Services/UserDatabases';

function isCIDatabase(databaseId) {
  return UserDatabases.isCIDatabase(databaseId);
}

function hasCollection(collection) {
  return Array.isArray(collection) && collection.length > 0;
}

function isReagentsVisible(detail) {
  if (!detail.stages) {
    return false;
  }
  return detail.stages.some((stage) => hasCollection(stage.reagents) || hasCollection(stage.solvents) || hasCollection(stage.catalysts));
}

function isReactantsProductsVisible(detail) {
  return hasCollection(detail.reactants) || hasCollection(detail.products);
}

function isReactantsProductsOnStageVisible(detail) {
  // Extra condition detail.stages && detail.stages.length <= 1 was added per story XFE-5682
  if (!detail.stages || detail.stages.length <= 1) {
    return false;
  }
  return detail.stages.some((stage) => isReactantsProductsVisible(stage));
}

function flatMap(stages, mapFn) {
  const targetCollections = without(map(stages, mapFn), undefined);
  return flatten(
    targetCollections.map((collection, stageIndex) => {
      if (!Array.isArray(collection)) return [];
      return collection.map((item) => {
        const cloned = { ...item };
        cloned.stage = (stageIndex + 1).toString();
        return cloned;
      });
    })
  );
}

function getDetailData(part, detail) {
  return detail[part];
}

function getDetailDataOnStagesLevel(part, detail) {
  const flattened = flatMap(detail.stages, (stage) => stage[part]);
  return sortBy(flattened, 'stage');
}

function getReagentsSolventsCatalystsOnStagesLevel(detail) {
  const extractData = (collection, role) =>
    flatMap(detail.stages, (stage) => {
      if (!Array.isArray(stage[collection])) return [];
      return stage[collection].map((item) => {
        const cloned = { ...item };
        cloned.role = role;
        return cloned;
      });
    });

  const reagents = extractData('reagents', 'Reagent');
  const solvents = extractData('solvents', 'Solvent');
  const catalysts = extractData('catalysts', 'Catalyst');

  let allData = [].concat(reagents, solvents, catalysts);
  allData = compact(allData);
  return sortBy(allData, 'stage');
}

function isAdditionalInfoVisible(detail) {
  return (
    isReactantsProductsVisible(detail) ||
    isReagentsVisible(detail) ||
    hasCollection(detail.protectingGroups) ||
    hasCollection(detail.metabolizationGroups)
  );
}

function getStage(row) {
  if (!row || !row.cells || !Array.isArray(row.cells)) {
    return null;
  }
  const stageCell = find(row.cells, (cell) => cell && cell.id === 'stage');
  return stageCell ? stageCell.value : null;
}

/**
 * @ngdoc service
 * @name app.results:ReactReactionAdditionalInfoHelper
 * @requires UserDatabases
 * @returns {object} helper service for React reaction additional info
 * @description helper service for React reaction additional info
 */
const ReactReactionAdditionalInfoHelper = {
  isAdditionalInfoVisible,
  isCIDatabase,
  isReagentsVisible,
  isReactantsProductsVisible,
  isReactantsProductsOnStageVisible,
  hasCollection,
  getStage,
  getDetailData,
  getDetailDataOnStagesLevel,
  getReagentsSolventsCatalystsOnStagesLevel,
};

export default ReactReactionAdditionalInfoHelper;
